import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import Select from 'react-select';
import { Search, User, GraduationCap, ExternalLink, ChevronLeft, ChevronRight, Plus, Minus, Maximize, Target, Eye } from 'lucide-react';

const ExpertGraph = ({ data, onNodeClick }) => {
    const graphRef = useRef(null);
    const [highlightNodes, setHighlightNodes] = useState(new Set());
    const [highlightLinks, setHighlightLinks] = useState(new Set());
    const [hoverNode, setHoverNode] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);

    const [searchName, setSearchName] = useState('');
    const [searchSkills, setSearchSkills] = useState([]);

    // New Region & Country Filters
    const [searchRegion, setSearchRegion] = useState('');
    const [searchCountry, setSearchCountry] = useState('');

    // New States
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [focusMode, setFocusMode] = useState(false);

    // Transform raw faculty data into graph nodes and links
    const graphData = useMemo(() => {
        const nodes = [];
        const links = [];
        const skillSet = new Set();
        const expertNodesMap = new Map();
        const skillNodesMap = new Map();

        if (!data || data.length === 0) return { nodes, links };

        data.forEach(expert => {
            // Create Expert Node
            const expertNode = {
                id: `expert-${expert.FacultyID}`,
                name: expert.Name,
                type: 'expert',
                val: 5, // Base size
                raw: expert
            };
            nodes.push(expertNode);
            expertNodesMap.set(expertNode.id, expertNode);

            // Process Skills
            if (expert.Skills && Array.isArray(expert.Skills)) {
                expert.Skills.forEach(skillName => {
                    const cleanSkillName = skillName.trim();
                    if (!cleanSkillName) return;

                    const skillId = `skill-${cleanSkillName.toLowerCase().replace(/\s+/g, '-')}`;

                    // Create Skill Node if it doesn't exist
                    if (!skillSet.has(skillId)) {
                        skillSet.add(skillId);
                        const skillNode = {
                            id: skillId,
                            name: cleanSkillName,
                            type: 'skill',
                            val: 2 // Base size, will grow with links
                        };
                        nodes.push(skillNode);
                        skillNodesMap.set(skillId, skillNode);
                    } else {
                        // Increase size of skill node for every expert that has it
                        const existingSkillNode = skillNodesMap.get(skillId);
                        if (existingSkillNode) {
                            existingSkillNode.val += 0.5;
                        }
                    }

                    // Create Link between Expert and Skill
                    const link = {
                        source: expertNode.id,
                        target: skillId
                    };
                    links.push(link);
                });
            }
        });

        // Pre-compute neighbors for instant hover/highlight resolution
        const nodeById = new Map();
        nodes.forEach(n => {
            n.neighbors = [];
            n.links = [];
            nodeById.set(n.id, n);
        });

        links.forEach(link => {
            // react-force-graph mutates links to reference obj eventually, but strings on creation
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;
            const source = nodeById.get(sourceId);
            const target = nodeById.get(targetId);
            if (source && target) {
                source.neighbors.push(target);
                target.neighbors.push(source);
                source.links.push(link);
                target.links.push(link);
            }
        });

        return { nodes, links };
    }, [data]);

    // Update highlights based on selectedNode, hoverNode or searchSkills
    const updateHighlights = useCallback((activeNode, activeSkills = [], activeRegion = '', activeCountry = '') => {
        setHighlightNodes(new Set());
        setHighlightLinks(new Set());

        if (activeNode) {
            const newHighlightNodes = new Set([activeNode.id]);
            const newHighlightLinks = new Set();

            if (activeNode.neighbors) {
                activeNode.neighbors.forEach(n => newHighlightNodes.add(n.id));
            }
            if (activeNode.links) {
                activeNode.links.forEach(l => newHighlightLinks.add(l));
            }

            setHighlightNodes(newHighlightNodes);
            setHighlightLinks(newHighlightLinks);
        } else if (activeSkills.length > 0 || activeRegion || activeCountry) {
            const newHighlightNodes = new Set(activeSkills);
            const newHighlightLinks = new Set();

            // Intersection Logic: Find experts that match all criteria
            graphData.nodes.forEach(node => {
                if (node.type === 'expert') {
                    let match = true;

                    if (activeSkills.length > 0) {
                        const expertSkillIds = new Set(node.neighbors.map(n => n.id));
                        if (!activeSkills.every(skillId => expertSkillIds.has(skillId))) match = false;
                    }

                    if (activeRegion && node.raw.Region !== activeRegion) match = false;
                    if (activeCountry && node.raw.Country !== activeCountry) match = false;

                    if (match) {
                        newHighlightNodes.add(node.id);
                        node.links.forEach(link => {
                            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
                            const targetId = typeof link.target === 'object' ? link.target.id : link.target;

                            if (activeSkills.length === 0 || activeSkills.includes(targetId) || activeSkills.includes(sourceId)) {
                                newHighlightLinks.add(link);
                            }
                        });
                        
                        // If no skills to filter by, but geographic constraints matched, highlight their connections
                        if (activeSkills.length === 0) {
                            node.neighbors.forEach(n => newHighlightNodes.add(n.id));
                            node.links.forEach(l => newHighlightLinks.add(l));
                        }
                    }
                }
            });

            setHighlightNodes(newHighlightNodes);
            setHighlightLinks(newHighlightLinks);
        }
    }, [graphData]);

    useEffect(() => {
        if (selectedNode) {
            updateHighlights(selectedNode, [], '', '');
        } else if (searchSkills.length > 0 || searchRegion || searchCountry) {
            updateHighlights(null, searchSkills, searchRegion, searchCountry);
        } else {
            updateHighlights(hoverNode, [], '', '');
        }
    }, [selectedNode, hoverNode, searchSkills, searchRegion, searchCountry, updateHighlights]);

    // Apply custom D3 forces for better clustering/gravity
    useEffect(() => {
        if (graphRef.current) {
            const chargeForce = graphRef.current.d3Force('charge');
            if (chargeForce) {
                // Increase repulsion slightly so nodes aren't too bunched up
                chargeForce.strength(-150);
            }
            const linkForce = graphRef.current.d3Force('link');
            if (linkForce) {
                // Keep link distance manageable
                linkForce.distance(40);
            }
        }
    }, [graphData]);

    const handleNodeHover = useCallback(node => {
        if (!selectedNode && searchSkills.length === 0) {
            setHoverNode(node || null);
        }
    }, [selectedNode, searchSkills]);

    const handleNodeClick = useCallback(node => {
        if (!node) return;

        if (selectedNode && selectedNode.id === node.id) {
            // Retract on second click
            setSelectedNode(null);
            setSearchName('');
            setSearchSkills([]);
            setSearchRegion('');
            setSearchCountry('');
        } else {
            // Lock onto new node
            setSelectedNode(node);

            // Sync selectors
            if (node.type === 'expert') {
                setSearchName(node.id);
                setSearchSkills([]);
                setSearchRegion('');
                setSearchCountry('');
            } else if (node.type === 'skill') {
                setSearchSkills([node.id]);
                setSearchName('');
                setSearchRegion('');
                setSearchCountry('');
            }

            // Center the camera on the node
            if (graphRef.current) {
                graphRef.current.centerAt(node.x, node.y, 1000);
                graphRef.current.zoom(4, 1000);
            }
        }
    }, [selectedNode]);

    const handleNameSelect = (option) => {
        const value = option ? option.value : '';
        setSearchName(value);
        setSearchSkills([]);
        setSearchRegion('');
        setSearchCountry('');
        if (value) {
            const expNode = graphData.nodes.find(n => n.type === 'expert' && n.id === value);
            if (expNode) handleNodeClick(expNode);
        } else {
            setSelectedNode(null);
        }
    };

    const handleSkillSelect = (options) => {
        const values = options ? options.map(opt => opt.value) : [];
        setSearchSkills(values);
        setSearchName('');
        setSelectedNode(null); // Clear single node selection when searching by multiple skills

        if (values.length > 0 && graphRef.current) {
            // Optional zoom out slightly when viewing multiple
            graphRef.current.zoomToFit(1000, 100);
        }
    };

    const handleRegionSelect = (option) => {
        const value = option ? option.value : '';
        setSearchRegion(value);
        setSearchName('');
        setSelectedNode(null);

        if (graphRef.current) graphRef.current.zoomToFit(1000, 100);
    };

    const handleCountrySelect = (option) => {
        const value = option ? option.value : '';
        setSearchCountry(value);
        setSearchName('');
        setSelectedNode(null);

        if (graphRef.current) graphRef.current.zoomToFit(1000, 100);
    };

    const expertOptions = useMemo(() => graphData.nodes.filter(n => n.type === 'expert').sort((a, b) => a.name.localeCompare(b.name)).map(n => ({ value: n.id, label: n.name })), [graphData]);
    const skillOptions = useMemo(() => graphData.nodes.filter(n => n.type === 'skill').sort((a, b) => a.name.localeCompare(b.name)).map(n => ({ value: n.id, label: n.name })), [graphData]);

    const regionOptions = useMemo(() => {
        const regions = new Set();
        graphData.nodes.forEach(n => {
            if (n.type === 'expert' && n.raw && n.raw.Region) regions.add(n.raw.Region);
        });
        return Array.from(regions).sort().map(r => ({ value: r, label: r }));
    }, [graphData]);

    const countryOptions = useMemo(() => {
        const countries = new Set();
        graphData.nodes.forEach(n => {
            if (n.type === 'expert' && n.raw && n.raw.Country) countries.add(n.raw.Country);
        });
        return Array.from(countries).sort().map(c => ({ value: c, label: c }));
    }, [graphData]);

    const selectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: '#1e293b',
            borderColor: '#334155',
            color: '#f8fafc',
            boxShadow: 'none',
            '&:hover': { borderColor: '#475569' }
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            zIndex: 9999
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#334155' : 'transparent',
            color: '#f8fafc',
            cursor: 'pointer',
            '&:active': { backgroundColor: '#475569' }
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#f8fafc'
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#38bdf8',
            borderRadius: '4px'
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#020617',
            fontWeight: 'bold',
            fontSize: '0.8rem'
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#020617',
            ':hover': {
                backgroundColor: '#0ea5e9',
                color: '#020617',
            },
        }),
        input: (provided) => ({
            ...provided,
            color: '#f8fafc'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#475569'
        })
    };

    const paintNode = useCallback((node, ctx, globalScale) => {
        const hasActiveSelection = selectedNode || hoverNode || searchSkills.length > 0 || searchRegion || searchCountry;
        const isHoveredOrSelected = selectedNode ? selectedNode.id === node.id : (hoverNode ? hoverNode.id === node.id : false);
        const isHighlighted = highlightNodes.has(node.id);
        const isMuted = hasActiveSelection && !isHighlighted;

        const size = node.val;

        ctx.beginPath();
        ctx.arc(node.x, node.y, size, 0, 2 * Math.PI, false);

        if (node.type === 'expert') {
            ctx.fillStyle = isMuted ? 'rgba(56, 189, 248, 0.1)' : (isHoveredOrSelected ? '#38bdf8' : '#0ea5e9');
            ctx.strokeStyle = isMuted ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)';
        } else {
            ctx.fillStyle = isMuted ? 'rgba(192, 132, 252, 0.1)' : ((isHoveredOrSelected || searchSkills.includes(node.id)) ? '#c084fc' : '#a855f7');
            ctx.strokeStyle = isMuted ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)';
        }

        ctx.lineWidth = 1.5 / globalScale;
        ctx.fill();
        ctx.stroke();

        if (globalScale > 2 || isHighlighted) {
            const fontSize = Math.max(3, 12 / globalScale);
            ctx.font = `${isHoveredOrSelected ? 'bold ' : ''}${fontSize}px Inter, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            ctx.fillStyle = isMuted ? 'rgba(148, 163, 184, 0.2)' : (node.type === 'expert' ? '#f8fafc' : '#cbd5e1');

            if (!isMuted) {
                ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
                ctx.shadowBlur = 4;
            }

            ctx.fillText(node.name, node.x, node.y + size + (fontSize / 2) + 2);
            ctx.shadowBlur = 0;
        }
    }, [hoverNode, selectedNode, highlightNodes, searchSkills, searchRegion, searchCountry]);

    const controlBtnStyle = {
        width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#1e293b', color: '#f8fafc', border: '1px solid #334155', borderRadius: '8px',
        cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
    };

    return (
        <div style={{ display: 'flex', width: '100%', height: '100%', background: '#020617', borderRadius: '16px', overflow: 'hidden', position: 'relative' }}>
            {/* Sidebar */}
            <div style={{
                width: sidebarOpen ? '320px' : '0px',
                minWidth: sidebarOpen ? '320px' : '0px',
                background: '#0f172a',
                borderRight: sidebarOpen ? '1px solid #1e293b' : 'none',
                display: 'flex',
                flexDirection: 'column',
                color: '#f8fafc',
                zIndex: 20,
                transition: 'all 0.3s ease',
                overflow: 'visible' // To allow dropdowns to pop out if needed
            }}>
                <div style={{ padding: '1.5rem', borderBottom: '1px solid #1e293b', opacity: sidebarOpen ? 1 : 0, transition: 'opacity 0.2s', overflow: 'visible' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                            <Search size={22} color="#38bdf8" /> Network Explorer
                        </h3>
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '8px', whiteSpace: 'nowrap' }}>Search by Expert (Single)</label>
                        <Select
                            options={expertOptions}
                            value={expertOptions.find(opt => opt.value === searchName) || null}
                            onChange={handleNameSelect}
                            isClearable
                            placeholder="Type to find expert..."
                            styles={selectStyles}
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '8px', whiteSpace: 'nowrap' }}>Requirements Filter (Intersection)</label>
                        <Select
                            isMulti
                            options={skillOptions}
                            value={skillOptions.filter(opt => searchSkills.includes(opt.value))}
                            onChange={handleSkillSelect}
                            placeholder="Must have all skills..."
                            styles={selectStyles}
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '8px', whiteSpace: 'nowrap' }}>Filter by Region</label>
                        <Select
                            options={regionOptions}
                            value={regionOptions.find(opt => opt.value === searchRegion) || null}
                            onChange={handleRegionSelect}
                            isClearable
                            placeholder="Select Region..."
                            styles={selectStyles}
                            classNamePrefix="react-select"
                        />
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', color: '#94a3b8', marginBottom: '8px', whiteSpace: 'nowrap' }}>Filter by Country</label>
                        <Select
                            options={countryOptions}
                            value={countryOptions.find(opt => opt.value === searchCountry) || null}
                            onChange={handleCountrySelect}
                            isClearable
                            placeholder="Select Country..."
                            styles={selectStyles}
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>

                <div style={{ padding: '1.5rem', flex: 1, overflowY: 'auto', opacity: sidebarOpen ? 1 : 0, transition: 'opacity 0.2s', overflowX: 'hidden' }}>
                    {selectedNode ? (
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
                                {selectedNode.type === 'expert' ? <User size={24} color="#38bdf8" style={{ minWidth: '24px' }} /> : <GraduationCap size={24} color="#c084fc" style={{ minWidth: '24px' }} />}
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {selectedNode.type === 'expert' ? 'Expert Profile' : 'Skill Node'}
                                    </div>
                                    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {selectedNode.name}
                                    </div>
                                </div>
                            </div>

                            {selectedNode.type === 'expert' && selectedNode.raw && (
                                <>
                                    <div style={{ fontSize: '0.9rem', color: '#cbd5e1', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                                        <div style={{ fontWeight: 600, color: '#f8fafc', marginBottom: '4px' }}>{selectedNode.raw.Designation}</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#94a3b8' }}>
                                            {selectedNode.raw.Organisation}
                                        </div>
                                    </div>

                                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                                        Connected to {Array.from(highlightNodes).filter(id => id.startsWith('skill-')).length} skills
                                    </div>

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '2rem' }}>
                                        {Array.from(highlightNodes)
                                            .filter(id => id.startsWith('skill-'))
                                            .map(skillId => {
                                                const skillNode = graphData.nodes.find(n => n.id === skillId);
                                                return skillNode ? (
                                                    <span key={skillId} style={{ background: '#334155', color: '#e9d5ff', padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                        {skillNode.name}
                                                    </span>
                                                ) : null;
                                            })}
                                    </div>

                                    <button
                                        onClick={() => onNodeClick && onNodeClick(selectedNode.raw)}
                                        style={{
                                            width: '100%', padding: '12px', background: '#38bdf8', color: '#020617',
                                            border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer',
                                            display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px',
                                            transition: 'background 0.2s', fontSize: '0.9rem'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#7dd3fc'}
                                        onMouseLeave={e => e.currentTarget.style.background = '#38bdf8'}
                                    >
                                        <ExternalLink size={18} /> View Full Profile
                                    </button>
                                </>
                            )}

                            {selectedNode.type === 'skill' && (
                                <div>
                                    <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '1rem', marginBottom: '1rem' }}>
                                        <div style={{ fontSize: '2rem', fontWeight: 800, color: '#c084fc', marginBottom: '0.25rem' }}>
                                            {Array.from(highlightNodes).filter(id => id.startsWith('expert-')).length}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#cbd5e1' }}>
                                            Experts possess this skill in the network.
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {Array.from(highlightNodes)
                                            .filter(id => id.startsWith('expert-'))
                                            .map(expertId => {
                                                const expertNode = graphData.nodes.find(n => n.id === expertId);
                                                return expertNode ? (
                                                    <div key={expertId} onClick={() => handleNodeClick(expertNode)} style={{ background: '#0f172a', padding: '8px 12px', borderRadius: '6px', border: '1px solid #334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                                                        <User size={14} color="#38bdf8" style={{ minWidth: '14px' }} />
                                                        <span style={{ color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{expertNode.name}</span>
                                                    </div>
                                                ) : null;
                                            })}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (searchSkills.length > 0 || searchRegion || searchCountry) ? (
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
                                <Target size={24} color="#c084fc" style={{ minWidth: '24px' }} />
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Intersection Search
                                    </div>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#f8fafc', lineHeight: 1.3 }}>
                                        Matches requirements: {Array.from(highlightNodes).filter(id => id.startsWith('expert-')).length}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {Array.from(highlightNodes)
                                    .filter(id => id.startsWith('expert-'))
                                    .map(expertId => {
                                        const expertNode = graphData.nodes.find(n => n.id === expertId);
                                        return expertNode ? (
                                            <div key={expertId} onClick={() => handleNodeClick(expertNode)} style={{ background: '#0f172a', padding: '8px 12px', borderRadius: '6px', border: '1px solid #334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                                                <User size={14} color="#38bdf8" style={{ minWidth: '14px' }} />
                                                <span style={{ color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{expertNode.name}</span>
                                            </div>
                                        ) : null;
                                    })}
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            color: '#475569', fontSize: '0.9rem', textAlign: 'center',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem',
                            marginTop: '2rem'
                        }}>
                            <Search size={32} color="#1e293b" />
                            Click a node in the graph or use the selectors above to focus and explore connections.
                        </div>
                    )}
                </div>
            </div>

            {/* Graph Container */}
            <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0, minWidth: 0 }}>
                {/* Sidebar Toggle Button */}
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 10, ...controlBtnStyle }}
                    title={sidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
                >
                    {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                </button>

                {/* Graph Controls Overlay */}
                <div style={{ position: 'absolute', top: '70px', right: '20px', display: 'flex', flexDirection: 'column', gap: '8px', zIndex: 10 }}>
                    <button
                        onClick={() => setFocusMode(!focusMode)}
                        style={{ ...controlBtnStyle, background: focusMode ? '#38bdf8' : '#1e293b', color: focusMode ? '#0f172a' : '#f8fafc', borderColor: focusMode ? '#38bdf8' : '#334155' }}
                        title="Toggle Focus Mode (Hide Unselected)"
                    >
                        <Eye size={18} />
                    </button>
                    <div style={{ height: '1px', background: '#334155', margin: '4px 0' }}></div>
                    <button onClick={() => { if (graphRef.current) graphRef.current.zoom(graphRef.current.zoom() * 1.5, 400); }} style={controlBtnStyle} title="Zoom In">
                        <Plus size={18} />
                    </button>
                    <button onClick={() => { if (graphRef.current) graphRef.current.zoom(graphRef.current.zoom() / 1.5, 400); }} style={controlBtnStyle} title="Zoom Out">
                        <Minus size={18} />
                    </button>
                    <button onClick={() => { if (graphRef.current) graphRef.current.zoomToFit(400, 50); }} style={controlBtnStyle} title="Fit to Screen">
                        <Maximize size={18} />
                    </button>
                </div>

                {graphData.nodes.length > 0 ? (
                    <ForceGraph2D
                        ref={graphRef}
                        graphData={graphData}
                        nodeRelSize={1}
                        nodeVisibility={node => {
                            if (!focusMode) return true;
                            const hasActiveSelection = selectedNode || searchSkills.length > 0 || searchRegion || searchCountry;
                            if (!hasActiveSelection) return true;
                            return highlightNodes.has(node.id);
                        }}
                        linkVisibility={link => {
                            if (!focusMode) return true;
                            const hasActiveSelection = selectedNode || searchSkills.length > 0 || searchRegion || searchCountry;
                            if (!hasActiveSelection) return true;
                            return highlightLinks.has(link);
                        }}
                        nodeLabel={node => `
                            <div style="background: rgba(15, 23, 42, 0.95); padding: 8px 12px; border-radius: 6px; border: 1px solid #334155; color: #f8fafc; font-family: Inter, sans-serif; box-shadow: 0 4px 6px rgba(0,0,0,0.5);">
                                <div style="font-weight: 700; font-size: 13px; margin-bottom: 4px;">${node.name}</div>
                                <div style="font-size: 11px; color: ${node.type === 'expert' ? '#38bdf8' : '#c084fc'}; text-transform: uppercase; letter-spacing: 0.5px;">${node.type === 'expert' ? 'Expert Profile' : 'Skill Area'}</div>
                            </div>
                        `}
                        nodeCanvasObject={paintNode}
                        nodePointerAreaPaint={(node, color, ctx) => {
                            ctx.fillStyle = color;
                            ctx.beginPath();
                            // Increase hit area explicitly to make smaller nodes much easier to hover/click
                            ctx.arc(node.x, node.y, node.val + 5, 0, 2 * Math.PI, false);
                            ctx.fill();
                        }}
                        linkColor={(link) => highlightLinks.has(link) ? '#cbd5e1' : 'rgba(51, 65, 85, 0.4)'}
                        linkWidth={(link) => highlightLinks.has(link) ? 2 : 1}
                        linkDirectionalParticles={(link) => highlightLinks.has(link) ? 4 : 0}
                        linkDirectionalParticleWidth={3}
                        linkDirectionalParticleSpeed={0.005}
                        onNodeHover={handleNodeHover}
                        onNodeClick={handleNodeClick}
                        d3AlphaDecay={0.02}
                        d3VelocityDecay={0.3}
                        cooldownTicks={100}
                        onEngineStop={() => graphRef.current?.zoomToFit(400, 50)}
                        backgroundColor="#020617"
                    />
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#475569' }}>
                        Not enough data to generate graph.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExpertGraph;
